import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Music, Upload, Plus, Check, ChevronDown, ChevronUp, GripVertical, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MusicManager = () => {
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicAlbums, setMusicAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState("Notary Hill G.N.W.");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [musicForm, setMusicForm] = useState({ title: '', artist: 'Notary Hill' });
  const [musicFile, setMusicFile] = useState(null);
  const musicFileRef = useRef(null);
  const [draggedTrack, setDraggedTrack] = useState(null);
  const [dragOverTrack, setDragOverTrack] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchAlbums = async () => {
    try {
      const res = await axios.get(`${API}/music/albums?include_drafts=true`);
      setMusicAlbums(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchTracks = async () => {
    try {
      const res = await axios.get(`${API}/music?album=${encodeURIComponent(selectedAlbum)}`);
      setMusicTracks(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAlbums(); }, []);
  useEffect(() => { fetchTracks(); }, [selectedAlbum]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!musicFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', musicForm.title);
      fd.append('artist', musicForm.artist);
      fd.append('album', selectedAlbum);
      fd.append('track_number', musicTracks.length + 1);
      fd.append('file', musicFile);
      await axios.post(`${API}/music/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMusicForm({ title: '', artist: 'Notary Hill' });
      setMusicFile(null);
      if (musicFileRef.current) musicFileRef.current.value = '';
      setShowUpload(false);
      fetchTracks();
    } catch (e) {
      console.error('Upload failed:', e);
      alert('Upload failed.');
    } finally { setUploading(false); }
  };

  const handleBulkUpload = async (files) => {
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      const fd = new FormData();
      fd.append('title', name);
      fd.append('artist', 'Notary Hill');
      fd.append('album', selectedAlbum);
      fd.append('track_number', musicTracks.length + i + 1);
      fd.append('file', file);
      try {
        await axios.post(`${API}/music/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { console.error(`Failed: ${file.name}`, err); }
    }
    setUploading(false);
    fetchTracks();
  };

  const deleteTrack = async (trackId) => {
    if (!window.confirm('Delete this track?')) return;
    try {
      await axios.delete(`${API}/music/${trackId}`);
      fetchTracks();
      fetchAlbums();
    } catch (e) { console.error('Delete failed:', e); }
  };

  const toggleRelease = async (albumName, currentReleased) => {
    try {
      await axios.put(`${API}/music/albums/${encodeURIComponent(albumName)}/release?release=${!currentReleased}`);
      fetchAlbums();
    } catch (e) { console.error(e); }
  };

  const handleDragStart = (id) => setDraggedTrack(id);
  const handleDragOver = (e, id) => { e.preventDefault(); setDragOverTrack(id); };
  const handleDragEnd = () => { setDraggedTrack(null); setDragOverTrack(null); };
  const handleDrop = async (targetId) => {
    if (!draggedTrack || draggedTrack === targetId) { handleDragEnd(); return; }
    const reordered = [...musicTracks];
    const fromIdx = reordered.findIndex(t => t.id === draggedTrack);
    const toIdx = reordered.findIndex(t => t.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setMusicTracks(reordered);
    handleDragEnd();
    try {
      await axios.put(`${API}/music/reorder`, { track_ids: reordered.map(t => t.id) });
    } catch (e) { console.error('Reorder failed:', e); fetchTracks(); }
  };

  return (
    <div className="mb-6 p-4 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="music-manager">
      <div className="flex items-center justify-between mb-1">
        <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 group" data-testid="music-collapse-toggle">
          {collapsed ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
          <Music size={18} className="text-cyan-500" />
          <h3 className="font-anton text-sm tracking-wider">MUSIC ALBUMS</h3>
          <span className="text-xs text-gray-500 ml-1">{musicTracks.length} tracks</span>
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowUpload(!showUpload)}
              className="flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded transition-colors"
              data-testid="asset-add-track-btn">
              <Plus size={14} /> Add Track
            </button>
            <label className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-gray-300 text-xs rounded transition-colors cursor-pointer" data-testid="asset-bulk-upload-label">
              <Upload size={14} /> Bulk Upload
              <input type="file" accept="audio/*" multiple className="hidden"
                onChange={(e) => e.target.files.length > 0 && handleBulkUpload(Array.from(e.target.files))} data-testid="asset-bulk-upload-input" />
            </label>
            {uploading && <span className="text-xs text-cyan-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading...</span>}
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="asset-album-tabs">
            {musicAlbums.map(a => (
              <div key={a.name} className="flex items-center gap-0">
                <button onClick={() => setSelectedAlbum(a.name)}
                  className={`px-3 py-1.5 text-xs font-mono rounded-l transition-all ${selectedAlbum === a.name ? 'bg-cyan-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                  data-testid={`asset-album-tab-${a.name}`}>
                  {a.name} ({a.track_count})
                </button>
                <button onClick={() => toggleRelease(a.name, a.released)}
                  className={`px-2 py-1.5 text-[10px] font-bold rounded-r border-l border-black/20 transition-all ${a.released ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                  title={a.released ? 'Click to hide from public' : 'Click to release publicly'}
                  data-testid={`album-release-toggle-${a.name}`}>
                  {a.released ? 'LIVE' : 'DRAFT'}
                </button>
              </div>
            ))}
          </div>

          {showUpload && (
            <form onSubmit={handleUpload} className="mb-4 p-3 bg-black/30 border border-white/5 rounded-lg space-y-2" data-testid="asset-add-track-form">
              <div className="flex gap-2">
                <input type="text" placeholder="Track title" value={musicForm.title} onChange={e => setMusicForm({ ...musicForm, title: e.target.value })} required
                  className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-white/15 rounded text-sm text-white focus:outline-none focus:border-cyan-500" data-testid="asset-track-title-input" />
                <input type="text" placeholder="Artist" value={musicForm.artist} onChange={e => setMusicForm({ ...musicForm, artist: e.target.value })} required
                  className="w-40 px-3 py-2 bg-[#1a1a1a] border border-white/15 rounded text-sm text-white focus:outline-none focus:border-cyan-500" data-testid="asset-track-artist-input" />
              </div>
              <div className="flex gap-2 items-center">
                <label className="flex-1 flex items-center gap-2 px-3 py-2 bg-black/30 border border-white/10 rounded text-sm cursor-pointer hover:border-cyan-500/50 transition-colors">
                  <Music size={14} className="text-gray-500" />
                  <span className={musicFile ? 'text-white text-xs' : 'text-gray-500 text-xs'}>{musicFile ? musicFile.name : 'Choose audio file...'}</span>
                  <input ref={musicFileRef} type="file" accept="audio/*" className="hidden" onChange={e => setMusicFile(e.target.files[0] || null)} data-testid="asset-track-file-input" />
                </label>
                <button type="submit" disabled={uploading || !musicFile || !musicForm.title}
                  className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
                  data-testid="asset-upload-track-btn">
                  <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button type="button" onClick={() => setShowUpload(false)} className="px-3 py-2 text-xs text-gray-400 hover:text-white transition-colors">Cancel</button>
              </div>
            </form>
          )}

          {musicTracks.length > 0 ? (
            <div className="space-y-1" data-testid="music-track-list">
              <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Drag to reorder</p>
              {musicTracks.map((track, idx) => (
                <div key={track.id} draggable
                  onDragStart={() => handleDragStart(track.id)} onDragOver={(e) => handleDragOver(e, track.id)}
                  onDrop={() => handleDrop(track.id)} onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 p-2.5 rounded-lg transition-all group cursor-grab active:cursor-grabbing ${
                    draggedTrack === track.id ? 'opacity-40 scale-95' : ''
                  } ${dragOverTrack === track.id && draggedTrack !== track.id ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-white/[0.03] border border-transparent hover:bg-white/[0.06]'}`}
                  data-testid={`music-track-${track.id}`}>
                  <GripVertical size={14} className="text-gray-600 flex-shrink-0" />
                  <span className="font-mono text-xs text-gray-500 w-6">{String(idx + 1).padStart(2, '0')}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{track.title}</p>
                    <p className="text-[10px] text-gray-500 truncate">{track.artist}</p>
                  </div>
                  {track.audio_url ? <Check size={14} className="text-green-500 flex-shrink-0" /> : <span className="text-[10px] text-gray-600">No audio</span>}
                  <button onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const newFree = !track.is_free;
                      await axios.patch(`${API}/music/${track.id}/free`, { is_free: newFree });
                      setMusicTracks(prev => prev.map(t => t.id === track.id ? { ...t, is_free: newFree } : t));
                    } catch (err) { console.error(err); }
                  }} className={`px-2 py-0.5 text-[10px] font-bold rounded transition-all flex-shrink-0 ${track.is_free ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                    data-testid={`track-free-toggle-${track.id}`}>
                    {track.is_free ? 'FREE' : 'PAID'}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); deleteTrack(track.id); }}
                    className="p-1 text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                    data-testid={`asset-delete-track-${track.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-600">
              <Music size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No music tracks yet</p>
              <p className="text-xs text-gray-700 mt-1">Upload audio files to build your album</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MusicManager;
